import React, { useContext, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { MainContext } from '../context/MainContext';
import { useDispatch, useSelector } from 'react-redux';
import { handleAddToCart, handleQtyDec } from '../utils/AddRemoveCartItems';
import LoginModal from '../components/LoginModal';
import ImageLightbox from '../components/ImageLightbox';
import ProductCard from '../components/ProductCard';
import { Home, ChevronRight, ShoppingCart, Minus, Plus, ShieldCheck, PackageCheck, Truck } from 'lucide-react';
import { baseUrl } from '../apis';

// Same category slug -> display name mapping used by the header/footer nav.
const categoryNames = {
  brackets: 'Brackets',
  bands_and_tubes: 'Bands & Tubes',
  wires_and_springs: 'Wires & Springs',
  elastomerics: 'Elastomerics',
  orthodontic_pliers: 'Orthodontic Pliers',
  miscellaneous: 'Miscellaneous',
};

const formatCategoryLabel = (category) =>
  categoryNames[category] ||
  category
    .replace(/_/g, ' ')
    .replace(/ and /gi, ' & ')
    .replace(/\b\w/g, (c) => c.toUpperCase());

const trustPoints = [
  { icon: ShieldCheck, label: 'Genuine Products' },
  { icon: PackageCheck, label: 'Secure Packaging' },
  { icon: Truck, label: 'Reliable Delivery' },
];

const SpecificProductPage = () => {
  const dispatch = useDispatch();
  const { products } = useContext(MainContext);
  const params = useParams();
  const cartItems = useSelector((state) => state.cart.cartItems);
  const [showModal, setShowModal] = useState(false);
  const [showImageLightbox, setShowImageLightbox] = useState(false);

  const onLoginRequired = () => {
    setShowModal(true);
  };

  if (!products || products.length === 0) {
    return <div className="text-center mt-10 text-lg">Loading...</div>;
  }

  const product = products.find((item) => item.name_id === params.product);
  if (!product) {
    return <div className="text-center mt-10 text-lg text-red-500">Product not found</div>;
  }

  const { _id, name, description, price, image, category } = product;
  const imageSrc = `${baseUrl}/images/${image}`;
  const currentQty = cartItems ? cartItems[_id] || 0 : 0;
  const categoryLabel = formatCategoryLabel(category);

  // Related products: derived entirely from the already-loaded catalog in
  // MainContext — no extra fetch. Same-category products are prioritized;
  // if fewer than 5 exist, remaining slots are filled with other products.
  // The two filters partition the catalog (each product matches exactly
  // one), so the current product is excluded once and nothing repeats.
  const sameCategoryProducts = products.filter(
    (item) => item._id !== _id && item.category === category
  );
  const otherProducts = products.filter(
    (item) => item._id !== _id && item.category !== category
  );
  const relatedProducts = [...sameCategoryProducts, ...otherProducts].slice(0, 5);

  return (
    <div className="min-h-screen bg-gray-50 px-4 py-6">
      <div className="max-w-6xl mx-auto">
        {/* Breadcrumb */}
        <nav aria-label="Breadcrumb" className="flex items-center gap-1.5 text-sm text-gray-500 mb-4 flex-wrap">
          <Link to="/" className="flex items-center gap-1 no-underline text-gray-500 hover:text-[#2D3092] transition-colors">
            <Home size={14} strokeWidth={2} />
            <span>Home</span>
          </Link>
          <ChevronRight size={14} className="text-gray-300" />
          <Link
            to={`/category/${category}`}
            className="no-underline text-gray-500 hover:text-[#2D3092] transition-colors"
          >
            {categoryLabel}
          </Link>
          <ChevronRight size={14} className="text-gray-300" />
          <span className="text-[#2D3092] font-medium truncate max-w-[60vw] sm:max-w-none">{name}</span>
        </nav>

        {/* Main product card */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 sm:p-6">
          <div className="grid md:grid-cols-2 gap-6 md:gap-10 items-start">
            {/* Product Image */}
            <div className="group w-full aspect-square rounded-2xl overflow-hidden bg-gradient-to-br from-[#E8F3FD] to-[#DCEEFB]">
              {/* Primary product image: intentionally NOT lazy-loaded — it's the
                  main visible content of this page. No secondary/gallery images
                  currently exist for this product to lazy-load. Clicking it
                  opens the same image enlarged in a lightbox. */}
              <img
                src={imageSrc}
                alt={name}
                className="w-full h-full object-contain p-6 cursor-zoom-in transition-transform duration-300 ease-out group-hover:scale-105"
                loading="eager"
                decoding="async"
                onClick={() => setShowImageLightbox(true)}
              />
            </div>

            <ImageLightbox
              show={showImageLightbox}
              src={imageSrc}
              alt={name}
              onClose={() => setShowImageLightbox(false)}
            />

            {/* Product Details */}
            <div className="flex flex-col">
              <Link
                to={`/category/${category}`}
                className="self-start no-underline inline-block px-3 py-1 rounded-full bg-indigo-50 text-[#2D3092] text-xs font-semibold uppercase tracking-wide"
              >
                {categoryLabel}
              </Link>

              <h1 className="mt-3 text-2xl sm:text-3xl font-bold text-gray-900">{name}</h1>

              <p className="mt-3 text-gray-600 text-sm sm:text-base leading-relaxed">{description}</p>

              <div className="mt-5">
                <p className="text-xs font-medium text-gray-400 uppercase tracking-wide">Price</p>
                <p className="text-3xl font-extrabold text-[#2D3092]">₹{price}</p>
              </div>

              {/* OTP Modal */}
              <LoginModal show={showModal} onClose={() => setShowModal(false)} />

              {/* Cart Actions */}
              <div className="mt-5">
                {currentQty === 0 ? (
                  <button
                    className="w-full sm:w-auto flex items-center justify-center gap-2 bg-[#2D3092] hover:bg-opacity-90 text-white font-semibold py-3 px-8 rounded-xl transition"
                    onClick={() => handleAddToCart(_id, dispatch, onLoginRequired)}
                  >
                    <ShoppingCart size={18} strokeWidth={2} />
                    Add to Cart
                  </button>
                ) : (
                  <div className="inline-flex items-center gap-3 border border-gray-200 rounded-xl p-1">
                    <button
                      className="w-9 h-9 flex items-center justify-center rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 transition"
                      onClick={() => handleQtyDec(_id, currentQty, dispatch)}
                      aria-label="Decrease quantity"
                    >
                      <Minus size={16} strokeWidth={2.25} />
                    </button>
                    <span className="min-w-[1.5rem] text-center text-lg font-bold text-gray-900">{currentQty}</span>
                    <button
                      className="w-9 h-9 flex items-center justify-center rounded-lg bg-[#2D3092] hover:bg-opacity-90 text-white transition"
                      onClick={() => handleAddToCart(_id, dispatch)}
                      aria-label="Increase quantity"
                    >
                      <Plus size={16} strokeWidth={2.25} />
                    </button>
                  </div>
                )}
              </div>

              {/* Trust row */}
              <div className="mt-6 pt-4 border-t border-gray-100 grid grid-cols-3 divide-x divide-gray-100">
                {trustPoints.map(({ icon: Icon, label }) => (
                  <div key={label} className="flex items-center gap-2 px-2 first:pl-0">
                    <Icon size={18} strokeWidth={1.75} className="text-[#2D3092] flex-shrink-0" />
                    <span className="text-xs sm:text-sm text-gray-600 font-medium leading-tight">{label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Related Products */}
        {relatedProducts.length > 0 && (
          <div className="mt-6 bg-white rounded-2xl border border-gray-100 shadow-sm p-4 sm:p-5">
            <div className="flex items-center justify-between gap-3 mb-4">
              <h2 className="text-base sm:text-lg md:text-xl font-bold text-[#1B1F4B]">Related Products</h2>
              <Link
                to={`/category/${category}`}
                className="flex-shrink-0 inline-flex items-center gap-1 text-xs sm:text-sm font-semibold text-[#2D3092] hover:text-indigo-700 no-underline bg-[#EDF1FC] hover:bg-indigo-100 px-3 py-1.5 sm:px-4 sm:py-2 rounded-full transition-colors"
              >
                View all {categoryLabel} <ChevronRight size={14} />
              </Link>
            </div>

            <div className="grid grid-cols-2 gap-3 sm:flex sm:flex-wrap sm:justify-center sm:gap-0">
              {relatedProducts.map((item) => (
                <ProductCard key={item._id} data={item} />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SpecificProductPage;
